import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Send, PlusCircle } from "lucide-react";
import { fmtDateTime } from "@/lib/time";

export default function AIAssistant() {
    const [sessionId, setSessionId] = useState(null);
    const [history, setHistory] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const scrollRef = useRef(null);

    const loadSessions = async () => {
        try { const { data } = await api.get("/ai/sessions"); setSessions(data); }
        catch { /* ignore */ }
    };

    const loadHistory = async (sid) => {
        try {
            const { data } = await api.get(`/ai/chat/${sid}`);
            setHistory(data);
            setSessionId(sid);
        } catch (e) { toast.error(apiError(e)); }
    };

    useEffect(() => { loadSessions(); }, []);
    useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [history, busy]);

    const send = async (e) => {
        e?.preventDefault();
        if (!input.trim() || busy) return;
        const msg = input.trim();
        setHistory(h => [...h, { role: "user", content: msg }]);
        setInput(""); setBusy(true);
        try {
            const { data } = await api.post("/ai/chat", { session_id: sessionId, message: msg });
            setSessionId(data.session_id);
            setHistory(h => [...h, { role: "assistant", content: data.reply }]);
            loadSessions();
        } catch (e) {
            toast.error(apiError(e));
            setHistory(h => h.slice(0, -1));
        } finally { setBusy(false); }
    };

    const newSession = () => { setSessionId(null); setHistory([]); };

    const suggestions = [
        "Draft a notice under Section 138 of the NI Act for a bounced cheque of ₹2 lakh.",
        "Explain Order VII Rule 11 CPC with a 2024 Supreme Court precedent.",
        "Outline the elements of negligence in Indian tort law.",
        "Summarise key obligations in a standard commercial lease deed in Tamil Nadu.",
    ];

    return (
        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
            {/* Sessions sidebar */}
            <aside className="lg:border-r lg:border-border lg:pr-6 order-2 lg:order-1">
                <div className="flex items-center justify-between mb-4 lg:mb-6">
                    <div className="overline">Sessions</div>
                    <button onClick={newSession} data-testid="ai-new-session" className="text-primary hover:text-primary"><PlusCircle className="w-4 h-4" strokeWidth={1.5}/></button>
                </div>
                <div className="space-y-1 max-h-[30vh] lg:max-h-[60vh] overflow-y-auto">
                    {sessions.length === 0 ? (
                        <div className="text-xs text-muted-foreground">No sessions yet.</div>
                    ) : sessions.map(s => (
                        <button key={s.session_id} onClick={() => loadHistory(s.session_id)} data-testid={`ai-session-${s.session_id}`}
                            className={`block w-full text-left px-3 py-2 text-xs ${s.session_id === sessionId ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}>
                            <div className="truncate">{s.last_message}</div>
                            <div className="text-[10px] text-muted-foreground font-mono mt-1">{fmtDateTime(s.last_at)}</div>
                        </button>
                    ))}
                </div>
            </aside>

            {/* Chat panel */}
            <section className="flex flex-col min-h-[70vh] lg:min-h-[80vh] order-1 lg:order-2">
                <div className="flex items-center gap-3 mb-6">
                    <Sparkles className="w-6 h-6 text-primary" strokeWidth={1.5}/>
                    <div>
                        <div className="overline">Claude Sonnet 4.5</div>
                        <h1 className="font-serif text-3xl">Legal AI assistant</h1>
                    </div>
                </div>

                <div ref={scrollRef} className="flex-1 border border-border bg-background overflow-y-auto p-6 mb-4 min-h-[400px]" data-testid="ai-chat-scroll">
                    {history.length === 0 ? (
                        <div className="space-y-6">
                            <p className="text-muted-foreground text-sm">Ask anything — case drafting, citations, contract review, courtroom strategy.</p>
                            <div className="grid sm:grid-cols-2 gap-2">
                                {suggestions.map((s, i) => (
                                    <button key={i} onClick={() => setInput(s)} data-testid={`ai-suggestion-${i}`}
                                        className="text-left text-xs border border-border hover:border-primary hover:text-primary p-4 transition-colors">
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {history.map((m, i) => (
                                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${i}`}>
                                    <div className={`max-w-[80%] px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
                                        m.role === "user"
                                            ? "bg-muted border border-border"
                                            : "bg-background border-l-2 border-primary pl-4"
                                    }`}>
                                        {m.content}
                                    </div>
                                </div>
                            ))}
                            {busy && <div className="text-xs text-primary flex items-center gap-2"><span className="w-2 h-2 bg-primary animate-pulse"></span> Thinking…</div>}
                        </div>
                    )}
                </div>

                <form onSubmit={send} className="flex gap-3">
                    <input
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        placeholder="Ask the legal AI…"
                        data-testid="ai-input"
                        className="flex-1 bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm placeholder-muted-foreground"
                    />
                    <button type="submit" disabled={busy || !input.trim()} data-testid="ai-send"
                        className="bg-primary text-primary-foreground hover:bg-primary px-5 py-3 font-medium disabled:opacity-50 transition-colors">
                        <Send className="w-4 h-4" strokeWidth={2}/>
                    </button>
                </form>
            </section>
        </div>
    );
}